import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { pdf } from '@react-pdf/renderer';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import TooltipTextPopupCustom from './child/TooltipTextPopupCustom';
import ClosedComplaintsReport from '../reports/ClosedComplaintsReport';


const TableDataLayerClosed = () => {
    const [complaints, setComplaints] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [qrCodes, setQrCodes] = useState({});
    const [downloading, setDownloading] = useState(false);
    const [fromDate, setFromDate] = useState(''); // datetime-local string
    const [toDate, setToDate] = useState('');     // datetime-local string
    const [overallCount, setOverallCount] = useState(0); // unfiltered total for the header badge
    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const navigate = useNavigate();
    const location = useLocation();
      const APP_BASE_URL = process.env.REACT_APP_URL;

    // Debounce the search box so we don't hit the API on every keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchComplaints = useCallback(async () => {
        setLoading(true);
        try {
            // datetime-local values carry no timezone; convert to ISO so the
            // server compares in the user's local time.
            const toIso = (v) => {
                const d = v ? new Date(v) : null;
                return d && !Number.isNaN(d.getTime()) ? d.toISOString() : undefined;
            };
            const response = await axios.get(`${API_BASE_URL}/backend/complaints/get-closed-complaints`, {
            headers: {
            'Content-Type': 'application/json'
            },
            params: {
                searchQuery: debouncedSearch || undefined,
                fromDate: toIso(fromDate),
                toDate: toIso(toDate),
            }
            });

            if (response.data.success) {
                setComplaints(response.data.data);
                setOverallCount(response.data.overallCount ?? response.data.data.length);
                generateQrCodes(response.data.data,APP_BASE_URL);
            } else {
                toast.error("Failed to fetch complaints.");
            }
        } catch (error) {
            console.error("Error fetching complaints:", error);
            toast.error("Error fetching complaints.");
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, APP_BASE_URL, debouncedSearch, fromDate, toDate]);

    // Generate every QR code in parallel and commit them to state ONCE, instead
    // of awaiting each sequentially and calling setState per item (which
    // re-rendered the whole card grid on every code).
    const generateQrCodes = async (complaintsData, APP_BASE_URL) => {
        const entries = await Promise.all(
            complaintsData.map(async (complaint) => {
                try {
                    const qrCodeData = `${APP_BASE_URL}/complains/view/${complaint._id}`;
                    const qrCodeUrl = await QRCode.toDataURL(JSON.stringify(qrCodeData));
                    return [complaint._id, qrCodeUrl];
                } catch (error) {
                    console.error("Error generating QR code:", error);
                    return [complaint._id, null];
                }
            })
        );
        setQrCodes(Object.fromEntries(entries));
    };

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    // The date a complaint was closed (statusHistory "Closed" entry; the backend
    // trims statusHistory to just that entry, but we still search it defensively).
    const getClosedDate = (complaint) => {
        const hist = complaint.statusHistory || [];
        const closed = [...hist].reverse().find(
            (s) => ['Closed', 'Probe Reply Submitted'].includes(s.status)
        ) || hist[hist.length - 1];
        const d = closed?.changedAt ? new Date(closed.changedAt) : null;
        return d && !Number.isNaN(d.getTime()) ? d : null;
    };

    // Search + closing-date range are applied by the API (see fetchComplaints);
    // the list AND the PDF both use this set.
    const filteredComplaints = complaints.filter((complaint) => complaint.status !== 'Probe Cases');

    const clearDateRange = () => { setFromDate(''); setToDate(''); };

    // Current local time in datetime-local format ("YYYY-MM-DDTHH:mm"), used as
    // the picker max so future dates/times are disabled.
    const pad2 = (n) => String(n).padStart(2, '0');
    const now = new Date();
    const nowLocal = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
    // `max` only greys out the picker; a future value can still be typed in, so clamp it.
    const clampToNow = (v) => (v && v > nowLocal ? nowLocal : v);

    // Download a PDF of the closed complaints currently shown — i.e. respecting
    // the search box AND the closing-date range, so the PDF always matches the list.
    const handleDownloadReport = async () => {
        if (filteredComplaints.length === 0) {
            toast.info('No Reply Submitted complaints match the current filter.');
            return;
        }
        setDownloading(true);
        try {
            const data = filteredComplaints.map((c) => ({
                complaint_number: c.complaint_number,
                complainant_name: c.complainant_name,
                complaint_reffered_by: c.complaint_reffered_by,
                complaint_detail: c.complaint_detail,
                dfo_remarks: c.dfo_remarks,
                closed_date: getClosedDate(c),
            }));
            const generatedAt = new Date();
            let qrCodeUrl = '';
            try {
                qrCodeUrl = await QRCode.toDataURL(`${APP_BASE_URL}/closed/list`, { width: 120 });
            } catch (qrErr) { /* QR is optional */ }

            const fmt = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
            const rangeLabel = (fromDate || toDate)
                ? `Reply Submitted between ${fmt(fromDate) || 'the beginning'} and ${fmt(toDate) || 'now'}`
                : '';

            const blob = await pdf(
                <ClosedComplaintsReport reportData={data} generatedAt={generatedAt} qrCodeUrl={qrCodeUrl} rangeLabel={rangeLabel} />
            ).toBlob();

            const pad = (n) => String(n).padStart(2, '0');
            const stamp = `${pad(generatedAt.getDate())}-${pad(generatedAt.getMonth() + 1)}-${generatedAt.getFullYear()}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Reply-Submitted-Complaints-List-${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Report downloaded.');
        } catch (error) {
            console.error('Error building closed report:', error);
            toast.error('Error building the report.');
        } finally {
            setDownloading(false);
        }
    };


    useEffect(() => {
        if (location.state?.showToast) {
            toast.success("Complaint Dispatched Successfully!");
        }
    }, [location.state]);

    const getPriorityClasses = (priority) => {
        switch (priority) {
            case "Low":
                return {
                    bg: 'bg-gradient-start-3',
                    border: 'border-info-300',
                    text: 'text-info-600',
                    glow: 'info-glow',
                    pulse: 'info-pulse',
                    wave: 'from-info-400 to-info-100',
                    ripple: 'info-100',
                    badge: 'bg-info-focus text-info-600',
                    gradient: 'bg-dark-info-gradient',
                    tooltip: 'tooltip-info'
                };
            case "Medium":
                return {
                    bg: 'bg-gradient-start-4',
                    border: 'border-success-300',
                    text: 'text-success-600',
                    glow: 'success-glow',
                    pulse: 'success-pulse',
                    wave: 'from-success-400/20 to-success-100/20',
                    ripple: 'success-100',
                    badge: 'bg-success-focus text-success-600',
                    gradient: 'bg-dark-success-gradient',
                    tooltip: 'tooltip-success'
                };
            case "High":
                return {
                    bg: 'bg-gradient-start-9',
                    border: 'border-warning-300/30',
                    text: 'text-warning-600',
                    glow: 'warning-glow',
                    pulse: 'warning-pulse',
                    wave: 'from-warning-400/20 to-warning-100/20',
                    ripple: 'warning-100',
                    badge: 'bg-warning-focus text-warning-600',
                    gradient: 'bg-dark-warning-gradient',
                    tooltip: 'tooltip-warning'
                };
            case "Critical":
                return {
                    bg: 'bg-gradient-start-5',
                    border: 'border-danger-300',
                    text: 'text-danger-600',
                    glow: 'danger-glow',
                    pulse: 'danger-pulse',
                    wave: 'from-danger-400 to-danger-100',
                    ripple: 'danger-100',
                    badge: 'bg-danger-focus text-danger-600',
                    gradient: 'bg-dark-danger-gradient',
                    tooltip: 'tooltip-danger'
                };
            default:
                return {
                    bg: 'bg-gradient-start-1',
                    border: 'border-secondary-300/30',
                    text: 'text-secondary-600',
                    glow: 'secondary-glow',
                    pulse: 'secondary-pulse',
                    wave: 'from-secondary-400/20 to-secondary-100/20',
                    ripple: 'secondary-100',
                    badge: 'bg-secondary text-secondary-600',
                    gradient: 'bg-dark-secondary-gradient'
                };
        }
    };

    const getTimeElapsed = (dateString) => {
        if (!dateString) return 'N/A';
        const now = new Date();
        const created = new Date(dateString);
        const diff = now - created;

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const handleEditComplain = (complaintId, e) => {
        if (e) {
            const button = e.currentTarget;
            const ripple = document.createElement('span');
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const x = e.clientX - rect.left - size / 2;
            const y = e.clientY - rect.top - size / 2;

            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${x}px`;
            ripple.style.top = `${y}px`;
            ripple.classList.add('ripple-effect');

            const priority = button.closest('.complaint-card').dataset.priority;
            const rippleColor = getPriorityClasses(priority).ripple;
            ripple.style.backgroundColor = `var(--${rippleColor})`;

            button.appendChild(ripple);

            setTimeout(() => {
                ripple.remove();
            }, 600);
        }

        navigate(`/closed/update/${complaintId}`);
    };

    return (
        <div className="basic-data-table ">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="card-header bg-transparent border-bottom-0 pb-0 ">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            Reply Submitted Complaints
                        </h2>
                        <p className="text-sm text-gray-500">
                            Needs to be approved or reopened after the reply is submitted
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-success btn-sm d-flex align-items-center gap-2"
                            onClick={handleDownloadReport}
                            disabled={downloading || loading || filteredComplaints.length === 0}
                            title="Download a PDF of the Reply Submitted complaints shown (respects the date range)"
                        >
                            {downloading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Preparing…
                                </>
                            ) : (
                                <>
                                    <Icon icon="mdi:file-pdf-box" style={{ fontSize: 18 }} /> Download PDF Report
                                </>
                            )}
                        </button>
                        <span className={`px-4 py-2 rounded-full text-xs font-semibold bg-warning-100/20 text-warning-800 border border-warning-300/30 warning-glow`}>
                            {overallCount} Reply Submitted
                        </span>
                    </div>
                </div>
            </div>
            <div className="card-body pt-0">
                {/* Filter toolbar — search + closing date-time range (also drives the PDF). */}
                <div className="mb-4 p-16 radius-12 bg-neutral-50 border border-neutral-200">
                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <div className="flex-grow-1" style={{ minWidth: '240px', maxWidth: '360px' }}>
                            <label htmlFor="closed-complaint-search" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:magnify" /> Search
                            </label>
                            <div className="position-relative">
                                <Icon
                                    icon="mdi:magnify"
                                    className="position-absolute top-50 translate-middle-y text-muted"
                                    style={{ left: '14px', fontSize: '20px', pointerEvents: 'none' }}
                                />
                                <input
                                    id="closed-complaint-search"
                                    type="search"
                                    className="form-control ps-5 py-2 bg-white"
                                    placeholder="Search by complaint number"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div style={{ minWidth: '210px' }}>
                            <label htmlFor="closed-from" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:calendar-start" /> Reply Submitted from
                            </label>
                            <input
                                id="closed-from"
                                type="datetime-local"
                                className="form-control py-2 bg-white"
                                value={fromDate}
                                max={toDate || nowLocal}
                                onChange={(e) => setFromDate(clampToNow(e.target.value))}
                            />
                        </div>
                        <div style={{ minWidth: '210px' }}>
                            <label htmlFor="closed-to" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:calendar-end" /> Reply Submitted to
                            </label>
                            <input
                                id="closed-to"
                                type="datetime-local"
                                className="form-control py-2 bg-white"
                                value={toDate}
                                min={fromDate || undefined}
                                max={nowLocal}
                                onChange={(e) => setToDate(clampToNow(e.target.value))}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-auto">
                            {(fromDate || toDate) && (
                                <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={clearDateRange} title="Clear date range">
                                    <Icon icon="mdi:close" /> Clear
                                </button>
                            )}
                            <span className="badge bg-primary-50 text-primary-600 border border-primary-100 px-12 py-8 radius-8 fw-semibold">
                                {filteredComplaints.length} shown
                            </span>
                        </div>
                    </div>
                </div>
                <div className="row">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <div 
                            key={index} 
                            className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6"
                            >
                              
                                  <div className="card h-full overflow-hidden border-0 rounded-xl shadow-xs hover:shadow-md transition-all duration-300">
                                    {/* Priority Top Border (you can use a thin red bar for critical) */}
                                    <div className="h-1 w-full bg-red-500" />

                                    <div className="card-body p-5">
                                        <div className="flex flex-col md:flex-row justify-between items-start w-full gap-4">
                                        {/* Left Section */}
                                        {[...Array(7)].map((_, idx) => (
                                            <div className="flex-1 w-full space-y-3">
                                                {/* Complaint ID and Badge */}
                                                <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                                                    <Skeleton height={24} width="160px" className="rounded" />
                                                    <Skeleton height={24} width="80px" className="rounded-full" />
                                                </div>

                                                
                                                <Skeleton height={20} width="200px" className="rounded" />
                                                <Skeleton height={16} width="140px" className="rounded" />
                                            </div>

                                        ))}
                                        
                                      
                                        </div>

                                    </div>
                                    </div>

                        
                            </div>
                        ))
                        ) : filteredComplaints.length > 0 ? (
                        filteredComplaints.map((complaint) => {
                            const priority = getPriorityClasses(complaint.priority_level);
                            const timeElapsed = getTimeElapsed(complaint.time_of_call);
                            const qrCodeUrl = qrCodes[complaint._id];
                            return (
                               <div key={complaint._id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6">
                                    <div 
                                        className={`complaint-card card h-full overflow-hidden border ${priority.bg} ${priority.border} rounded-xl bg-white/80 backdrop-blur-sm relative ${priority.glow} ${priority.pulse}`}
                                        onClick={(e) => handleEditComplain(complaint._id, e)}
                                        data-priority={complaint.priority_level}
                                    >
                                        {/* Priority indicator top border */}
                                        <div className={`absolute top-0 left-0 w-full h-1 ${priority.gradient}`}></div>
                                           

                                        <div className="card-body p-5">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
                                                {/* Left Content - takes remaining space */}
                                                <div className="flex-grow-1 me-md-3 mb-3 mb-md-0">
                                                    {/* Complaint Number and Status */}
                                                    <div className="d-flex flex-wrap align-items-center mb-2">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-0 me-3" style={{whiteSpace: 'nowrap'}}>
                                                            #{complaint.complaint_number || 'N/A'}
                                                        </h3>
                                                        <div className="d-flex flex-column">
                                                            <span className="badge text-xs font-semibold px-3 py-1 rounded-full me-2 mb-1 status-reply-submitted-solid">
                                                                <Icon icon="mdi:check-circle-outline" className="me-1" /> Reply Submitted
                                                            </span>
                                                            <span className={`badge text-xs font-semibold ${priority.gradient} px-3 py-1 rounded-full text-white me-2 mb-1`}>
                                                                {complaint.priority_level || 'N/A'}
                                                            </span>
                                                            <span className={`text-xs font-semibold ${priority.text} d-flex align-items-center`}>
                                                                <Icon icon="mdi:clock-outline" className="me-1" style={{ lineHeight: 1 }} />
                                                               Elapsed from time of call {timeElapsed}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Department Info with added space */}
                                                    <table className="w-100 mb-3"> {/* Added mb-3 for space after department */}
                                                        <tbody>
                                                            <tr className="border-top">
                                                                <td className="py-2 text-muted fw-medium w-25" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Icon icon="bi:building" className="me-2 text-muted align-middle" /> 
                                                                    <span className="align-middle">Department:</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <span className="text-body">{complaint.dept_id?.name || 'N/A'}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Right Content - QR Code (keeps its original width) */}
                                                <div className="flex-shrink-0">
                                                     {qrCodeUrl && (
                                                <div className="flex-shrink-0">
                                                <a
                                                    href={`${APP_BASE_URL}/complains/view/${complaint._id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none text-reset" style={{zIndex: 9}}
                                                >
                                                    <div
                                                    className="qr-code-container"
                                                    style={{
                                                        width: '90px',
                                                        height: '90px',
                                                        background: 'white',
                                                        padding: '4px',
                                                        borderRadius: '6px',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        cursor: 'pointer'
                                                    }}
                                                    >
                                                    <img
                                                        src={qrCodeUrl}
                                                        alt="Complaint QR Code"
                                                        style={{ 
                                                        width: '100%', 
                                                        height: '100%', 
                                                        objectFit: 'contain'
                                                        }}
                                                        onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = '';
                                                        e.target.parentElement.style.display = 'flex';
                                                        e.target.parentElement.innerHTML = `
                                                            <div style="text-align: center; padding: 10px;">
                                                            <div style="font-size: 14px; margin-bottom: 8px;">QR Code Error</div>
                                                            <div style="font-size: 12px; color: #666;">Click to view complaint</div>
                                                            </div>`;
                                                        }}
                                                    />
                                                    </div>
                                                </a>
                                                </div>
                                            )}
                                                </div>
                                            </div>
                                           
                                            {/* Complaint details in table format */}
                                           <div className="mt-4 border-top pt-3" >
                                                <table className="table table-responsive scroll-sm">
                                                    <tbody>
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">District</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.complainant_district_id?.name || complaint.district_id?.name || complaint.complaint_district_id?.name || 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:calendar" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Referred By</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.complaint_reffered_by || 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:clock" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Date of Call</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.time_of_call ? new Date(complaint.time_of_call).toLocaleString() : 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:file-earmark-text" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Complaint Nature</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.complaint_nature_id ? complaint.complaint_nature_id?.name : 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:house" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Reply Submitted By:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.by || 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Closing Remarks:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.userRemarks || 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>
                                                        
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:sticky" className="me-2 text-muted align-middle" /> 
                                                                <span className="align-middle">Closing Time</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom 
                                                                    text={complaint.statusHistory?.[complaint.statusHistory.length - 1]?.changedAt || 'N/A'} 
                                                                    truncateLength={15} 
                                                                    placement="right" 
                                                                    customClass={`${priority.tooltip}`} 
                                                                />
                                                            </td>
                                                        </tr>

                                                        {/* =====================================================================
                                                            FEEDBACK DETAILS SECTION
                                                            ---------------------------------------------------------------------
                                                            Shows the feedback the complainant left after their complaint was
                                                            closed. All values come from `complaint.review`, which may be
                                                            undefined if the complainant never submitted feedback — hence the
                                                            optional chaining (`?.`) and 'N/A' / 0 fallbacks throughout.
                                                            Contains three rows: Rating (stars), Remarks (text), and any
                                                            uploaded Evidence/Attachment.
                                                        ===================================================================== */}

                                                        {/* Section divider: a horizontal rule with the centered
                                                            "Feedback Details" label separating the closing info above
                                                            from the complainant's feedback rows below. */}
                                                        {/* <tr className="border-bottom section-divider">
                                                            <td colSpan="2" className={`py-2 ${priority.bg} bg-opacity-50`} style={{backgroundColor: 'rgba(0,0,0,0.02)'}}>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="flex-grow-1 border-top border-muted opacity-25"></div>
                                                                    <span className="mx-2 text-xs fw-bold text-muted text-uppercase" style={{fontSize: '10px', letterSpacing: '1px'}}>Feedback Details</span>
                                                                    <div className="flex-grow-1 border-top border-muted opacity-25"></div>
                                                                </div>
                                                            </td>
                                                        </tr> */}

                                                        {/* Row 1 — Complainant Rating: renders a 5-star scale.
                                                            Loops over [1..5] and fills each star when its position is
                                                            <= the saved rating (complaint.review.rating, default 0),
                                                            otherwise shows an empty star outline. */}
                                                        {/* <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:star" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Complainant Rating</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                                        <Icon
                                                                            key={star}
                                                                            icon={star <= (complaint.review?.rating || 0) ? "bi:star-fill" : "bi:star"}
                                                                            className="text-warning"
                                                                            style={{ fontSize: '14px' }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr> */}

                                                        {/* Row 2 — Complainant Remarks: free-text feedback comment.
                                                            Shown via TooltipTextPopupCustom, which truncates the text to
                                                            15 chars and reveals the full remark in a tooltip on hover. */}
                                                        {/* <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:chat-left-text" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Complainant Remarks</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.review?.remarks || 'N/A'}
                                                                    truncateLength={15}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr> */}

                                                        {/* Row 3 — Evidence/Attachment: optional file the complainant
                                                            attached with their feedback.
                                                            If review.evidence_url exists, show a "View Attachment" button
                                                            that opens the file in a new tab; otherwise show 'N/A'.
                                                            onClick stopPropagation prevents the card's own click handler
                                                            from firing when the link is clicked. */}
                                                        {/* <tr>
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                <Icon icon="bi:paperclip" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Evidence/Attachment</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{whiteSpace: 'nowrap'}}>
                                                                {complaint.review?.evidence_url ? (
                                                                    <a
                                                                        href={complaint.review.evidence_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-outline-primary py-0 px-2"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{fontSize: '11px'}}
                                                                    >
                                                                        View Attachment
                                                                    </a>
                                                                ) : 'N/A'}
                                                            </td>
                                                        </tr> */}
                                                        {/* ===== END FEEDBACK DETAILS SECTION ===== */}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 text-center py-5 text-gray-400">
                            {searchTerm.trim()
                                ? `No Reply Submitted complaint found for "${searchTerm.trim()}".`
                                : (fromDate || toDate)
                                    ? 'No Reply Submitted complaints in the selected date range.'
                                    : 'You are all done! No Reply Submitted complaints to verify.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TableDataLayerClosed;
